import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://ovarddigital.com";
const SITE_NAME = "OVARD Digital";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["Organization", "LocalBusiness", "MarketingAgency"],
  "@id": `${SITE_URL}/#organization`,
  "name": "OVARD Digital",
  "alternateName": ["OVARD", "Ovard Digital"],
  "url": SITE_URL,
  "logo": {
    "@type": "ImageObject",
    "url": `${SITE_URL}/favicon.png`,
    "width": 64,
    "height": 64
  },
  "image": DEFAULT_IMAGE,
  "description": "OVARD Digital is a full-service digital marketing and brand growth agency based in Mysore, Karnataka, India. We build brands, websites, content and marketing systems for businesses across India.",
  "foundingDate": "2026",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Mysore",
    "addressRegion": "Karnataka",
    "postalCode": "570001",
    "addressCountry": "IN"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "12.2958",
    "longitude": "76.6394"
  },
  "areaServed": [
    { "@type": "Country", "name": "India" },
    { "@type": "City", "name": "Mysore" },
    { "@type": "City", "name": "Bangalore" },
    { "@type": "City", "name": "Mysuru" }
  ],
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "telephone": "+91-79750-34116",
      "contactType": "customer service",
      "availableLanguage": ["English", "Kannada", "Hindi"]
    },
    {
      "@type": "ContactPoint",
      "email": "ovarddigital@gmail.com",
      "contactType": "customer service"
    }
  ],
  "sameAs": [
    "https://www.instagram.com/ovardigital/",
    "https://www.linkedin.com/company/ovard-digital/",
    "https://wa.me/917975034116"
  ],
  "knowsAbout": [
    "Digital Marketing",
    "Brand Identity",
    "Website Development",
    "Social Media Marketing",
    "Meta Ads",
    "Google Ads",
    "SEO",
    "Content Creation",
    "E-commerce Development",
    "Marketing Strategy",
    "Marketing Automation",
    "AI Marketing Systems"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Digital Marketing Services",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Brand Identity", "provider": { "@id": `${SITE_URL}/#organization` } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Website Development", "provider": { "@id": `${SITE_URL}/#organization` } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "E-Commerce Development", "provider": { "@id": `${SITE_URL}/#organization` } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Content Creation", "provider": { "@id": `${SITE_URL}/#organization` } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Social Media Management", "provider": { "@id": `${SITE_URL}/#organization` } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Performance Marketing", "provider": { "@id": `${SITE_URL}/#organization` } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "SEO", "provider": { "@id": `${SITE_URL}/#organization` } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Marketing Strategy", "provider": { "@id": `${SITE_URL}/#organization` } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Marketing Automation", "provider": { "@id": `${SITE_URL}/#organization` } } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI Business Systems", "provider": { "@id": `${SITE_URL}/#organization` } } }
    ]
  }
};

const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  "url": SITE_URL,
  "name": "OVARD Digital",
  "description": "OVARD Digital — digital marketing and brand growth agency in Mysore, India.",
  "publisher": { "@id": `${SITE_URL}/#organization` },
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SITE_URL}/blog?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

/**
 * SEO component
 *
 * title      — page-specific title (do NOT append "| OVARD Digital", this component handles that)
 * description — page meta description
 * canonical  — path only, e.g. "/services" or "/" for home
 * ogImage    — absolute URL of OG image
 * ogType     — "website" | "article"
 * noindex    — set true to noindex a page
 * schema     — page-specific JSON-LD object (or array)
 * breadcrumbs — array of { name, path } for BreadcrumbList
 * isHomepage  — pass true on the homepage to suppress " | OVARD Digital" suffix
 */
export default function SEO({
  title,
  description,
  canonical,
  ogImage = DEFAULT_IMAGE,
  ogType = "website",
  noindex = false,
  schema = null,
  breadcrumbs = null,
  isHomepage = false,
}) {
  // Avoid "OVARD Digital | OVARD Digital" on homepage
  const fullTitle = !title
    ? "OVARD Digital — Digital Marketing & Brand Growth Agency India"
    : isHomepage
    ? title
    : `${title} | OVARD Digital`;

  const fullCanonical = canonical
    ? `${SITE_URL}${canonical === "/" ? "/" : canonical}`
    : SITE_URL;

  const metaDescription = description
    || "OVARD Digital is a full-service digital marketing and brand growth agency in Mysore, India. Brand identity, websites, content, performance marketing and AI systems — built around your business.";

  const breadcrumbSchema = breadcrumbs
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((item, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": item.name,
          "item": `${SITE_URL}${item.path}`
        }))
      }
    : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={fullCanonical} />
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      }

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@ovardigital" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Organization + WebSite schema on every page */}
      <script type="application/ld+json">
        {JSON.stringify(ORGANIZATION_SCHEMA)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(WEBSITE_SCHEMA)}
      </script>

      {/* Page-specific schema (single object or array of objects) */}
      {schema && (
        Array.isArray(schema)
          ? schema.map((s, i) => (
              <script key={i} type="application/ld+json">
                {JSON.stringify(s)}
              </script>
            ))
          : (
              <script type="application/ld+json">
                {JSON.stringify(schema)}
              </script>
            )
      )}

      {/* Breadcrumb schema */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
}
